import {
  ApplicationEnum,
  TemplateFormEnum,
  TypeEvalEnum,
} from "../../../convex/schema";

export { ApplicationEnum };

/**
 * Catalogue des applications : les épreuves qu'une organisation peut proposer
 * dans ses campagnes. Le super-admin choisit celles de chaque organisation ; le
 * client compose sa campagne parmi elles.
 *
 * En code et pas en table : une application est du code (une app candidat, un
 * calcul de score, un rapport), en ajouter une demande de toute façon un
 * déploiement.
 */
export interface ApplicationConfig {
  cle: ApplicationEnum;
  libelle: string;
  /** Ce qu'est l'épreuve, en quelques mots (affiché entre parenthèses). */
  description: string;
  typeEval: TypeEvalEnum;
  /** Template fixe de l'épreuve. Absent : choisi à la création (variante Inbasket, scorecard). */
  template?: TemplateFormEnum;
  /** Ce qui reste à choisir en créant la campagne, s'il y a lieu. */
  choixALaCreation?: string;
}

/** Dans l'ordre d'affichage, qui est aussi l'ordre de passage par défaut. */
export const APPLICATIONS: readonly ApplicationConfig[] = [
  {
    cle: ApplicationEnum.INBASKET,
    libelle: "Inbasket",
    description: "mise en situation sur une boîte mail",
    typeEval: TypeEvalEnum.INBASKET,
    choixALaCreation: "Variante complète ou rapide",
  },
  {
    cle: ApplicationEnum.AUTODIAG,
    libelle: "Autodiag",
    description: "autodiagnostic des compétences",
    typeEval: TypeEvalEnum.FORM,
    template: TemplateFormEnum.AUTODIAG,
  },
  {
    cle: ApplicationEnum.EGOGRAMME,
    libelle: "Égogramme",
    description: "profil d'analyse transactionnelle",
    typeEval: TypeEvalEnum.FORM,
    template: TemplateFormEnum.EGOGRAMME,
  },
  {
    cle: ApplicationEnum.PAMI,
    libelle: "PAMI",
    description: "test de personnalité et de motivations",
    typeEval: TypeEvalEnum.FORM,
    template: TemplateFormEnum.PAMI,
  },
  {
    cle: ApplicationEnum.COPSOQ3,
    libelle: "COPSOQ III",
    description: "questionnaire des risques psychosociaux",
    typeEval: TypeEvalEnum.FORM,
    template: TemplateFormEnum.COPSOQ3,
  },
  {
    cle: ApplicationEnum.FIT_CULTUREL,
    libelle: "PC Fit",
    description: "fit culturel",
    typeEval: TypeEvalEnum.FORM,
    template: TemplateFormEnum.FIT_CULTUREL,
  },
  {
    cle: ApplicationEnum.ENT_STRUCT,
    libelle: "FairView",
    description: "entretien structuré",
    typeEval: TypeEvalEnum.ENT_STRUCT,
    choixALaCreation: "Scorecard de l'organisation",
  },
];

export const TOUTES_LES_APPLICATIONS: readonly ApplicationEnum[] =
  APPLICATIONS.map((a) => a.cle);

export function libelleApplication(cle: ApplicationEnum): string {
  return APPLICATIONS.find((a) => a.cle === cle)?.libelle ?? cle;
}

/**
 * Application à laquelle appartient une entrée de `evaluationsConfig`.
 * `null` pour ce qui n'est pas une épreuve proposable : conteneur
 * `resultats`, évaluations-référentiels, template inconnu.
 */
export function applicationDe(config: {
  typeEval: string;
  template?: string;
}): ApplicationEnum | null {
  if (config.typeEval === TypeEvalEnum.INBASKET) return ApplicationEnum.INBASKET;
  if (config.typeEval === TypeEvalEnum.ENT_STRUCT)
    return ApplicationEnum.ENT_STRUCT;
  if (config.typeEval !== TypeEvalEnum.FORM) return null;
  return (
    APPLICATIONS.find(
      (a) => a.typeEval === TypeEvalEnum.FORM && a.template === config.template,
    )?.cle ?? null
  );
}

/** Sans doublon, dans l'ordre du catalogue. */
export function trierApplications(
  applications: Iterable<ApplicationEnum>,
): ApplicationEnum[] {
  const presentes = new Set(applications);
  return TOUTES_LES_APPLICATIONS.filter((cle) => presentes.has(cle));
}

/**
 * Applications qui composent une campagne, dans l'ordre du catalogue. Ce qui
 * n'est pas une épreuve proposable (cf. `applicationDe`) est ignoré.
 */
export function applicationsDeComposition(
  evaluationsConfig: readonly { typeEval: string; template?: string }[],
): ApplicationEnum[] {
  return trierApplications(
    evaluationsConfig
      .map(applicationDe)
      .filter((cle): cle is ApplicationEnum => cle !== null),
  );
}

/** « PAMI + FairView » : ce qui remplace le type de parcours à l'affichage. */
export function libelleComposition(
  evaluationsConfig: readonly { typeEval: string; template?: string }[],
): string {
  return applicationsDeComposition(evaluationsConfig)
    .map(libelleApplication)
    .join(" + ");
}
